// Active sessions (/settings/sessions, docs/06 §2): the session list with
// per-session terminate + "terminate all others". Each session's protocol
// `kind` is rendered as a human label rather than the raw enum value.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { Session } from '@merkury/protocol';
import { useAccount } from '../../shell/accountContext.js';
import { getLang, translate, useT } from '../../i18n/index.js';
import { useScreens } from '../ScreensContext.js';
import { Section, SettingsSubScreen, quietButtonClass } from './chrome.js';
import { useSignOutFlow, SignOutWarning } from './signOutFlow.js';

/** Human label for a session's platform kind (never surface the raw enum). */
export function sessionKindLabel(kind: Session['kind'] | string): string {
  switch (kind) {
    case 'android':
      return translate(getLang(), 'sessions.kindAndroid');
    case 'web':
      return translate(getLang(), 'sessions.kindWeb');
    default:
      return kind;
  }
}

function SessionsSection(): ReactNode {
  const t = useT();
  const { api } = useScreens();
  const [sessions, setSessions] = useState<Session[]>([]);
  const current = sessions.find((s) => s.current);

  const reload = useCallback(async () => {
    const res = await api.listSessions().catch(() => null);
    if (res) setSessions(res.sessions);
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Ending THIS device's own session is the same action as the explicit Log
  // Out flow (AccountSettingsScreen): it wipes the identity/session stored on
  // this device, so it needs the same confirm + recovery warning, and lands
  // on the same place afterwards — reused via signOutFlow.ts rather than a
  // second, silent implementation. Revoking the session server-side runs
  // first (best-effort — see useSignOutFlow), local wipe happens regardless.
  const endThisSession = useSignOutFlow(
    useCallback(
      () => (current ? api.terminateSession(current.id) : Promise.resolve()),
      [api, current],
    ),
  );

  return (
    <Section title={t('sessions.active')}>
      <ul className="flex flex-col gap-2">
        {sessions.map((s) => (
          <li key={s.id} className="flex items-center gap-3 rounded-xl bg-raised p-3">
            <div className="flex-1">
              <p className="text-sm text-ink">
                {s.label || sessionKindLabel(s.kind)}
                {s.current && <span className="ml-2 text-xs text-ok">{t('sessions.thisDevice')}</span>}
              </p>
              <p className="text-xs text-muted">
                {t('sessions.lastSeenLine', {
                  kind: sessionKindLabel(s.kind),
                  when: s.lastSeenAt ?? t('sessions.unknown'),
                })}
              </p>
            </div>
            {s.current ? (
              !endThisSession.confirming && (
                <button
                  type="button"
                  aria-label={t('sessions.terminateAria', {
                    name: s.label || sessionKindLabel(s.kind),
                  })}
                  onClick={endThisSession.requestConfirm}
                  className="min-h-touch rounded-xl bg-danger px-3 text-sm text-ink"
                >
                  {t('sessions.terminate')}
                </button>
              )
            ) : (
              <button
                type="button"
                aria-label={t('sessions.terminateAria', {
                  name: s.label || sessionKindLabel(s.kind),
                })}
                onClick={async () => {
                  await api.terminateSession(s.id).catch(() => undefined);
                  await reload();
                }}
                className="min-h-touch rounded-xl bg-danger px-3 text-sm text-ink"
              >
                {t('sessions.terminate')}
              </button>
            )}
          </li>
        ))}
      </ul>
      {current && endThisSession.confirming && <SignOutWarning flow={endThisSession} />}
      {sessions.some((s) => !s.current) && (
        <button
          type="button"
          onClick={async () => {
            await api.terminateOtherSessions().catch(() => undefined);
            await reload();
          }}
          className={quietButtonClass}
        >
          {t('sessions.terminateAll')}
        </button>
      )}
    </Section>
  );
}

export function SessionsSettingsScreen(): ReactNode {
  const t = useT();
  const { state } = useAccount();
  if (state.status !== 'ready') {
    return (
      <main className="mx-auto flex min-h-full w-full max-w-app items-center justify-center p-6">
        <p className="text-sm text-muted" role="status">
          {t('common.loading')}
        </p>
      </main>
    );
  }
  // Sessions are server-backed; an unregistered device has none.
  if (!state.account.userId) return <Navigate to="/settings" replace />;

  return (
    <SettingsSubScreen section="sessions" title={t('sessions.title')}>
      <SessionsSection />
    </SettingsSubScreen>
  );
}
